import { DeleteForeverRounded, DeleteOutline } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    IconButton,
    TableBody,
    alpha,
} from '@mui/material';
import { ChangeEvent, MouseEvent, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { CustomListItem, getCustomListCatalogItem } from 'entities/CustomList';
import {
    CatalogProductTable,
    CatalogProductTableHead,
    CatalogProductTableRow,
} from 'shared/ui/CatalogProductTable';

import { CustomListItemQuantityCell } from './CustomListItemQuantityCell';

interface CustomListDetailItemsTableProps {
    listId: string;
    items: CustomListItem[];
    selectedIds: string[];
    canManage: boolean;
    onToggleAll: (event: ChangeEvent<HTMLInputElement>) => void;
    onToggleRow: (itemId: string) => void;
    onOpenRemoveMenu: (
        event: MouseEvent<HTMLElement>,
        listItemIds: string[],
    ) => void;
    onExpandItem: (catalogItem: CatalogItem) => void;
}

export const CustomListDetailItemsTable = memo(
    ({
        listId,
        items,
        selectedIds,
        canManage,
        onToggleAll,
        onToggleRow,
        onOpenRemoveMenu,
        onExpandItem,
    }: CustomListDetailItemsTableProps) => {
        const { t } = useTranslation();

        const allVisibleSelected =
            items.length > 0 &&
            items.every((item) => selectedIds.includes(item.id));
        const someVisibleSelected =
            selectedIds.length > 0 &&
            items.some((item) => selectedIds.includes(item.id));

        return (
            <Box
                sx={{
                    borderRadius: '12px',
                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.3),
                    padding: '8px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                }}
            >
                {canManage && selectedIds.length > 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginBottom: '16px',
                            gap: '2px',
                            alignItems: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Button
                            variant="text"
                            onClick={(e) => onOpenRemoveMenu(e, selectedIds)}
                            data-testclass="custom-list-bulk-remove"
                            aria-label={t('Remove')}
                            startIcon={
                                <DeleteForeverRounded
                                    sx={{
                                        fontSize: '20px',
                                        width: '20px',
                                        height: '20px',
                                    }}
                                />
                            }
                            sx={(theme) => ({
                                padding: '6px 10px',
                                color: alpha(theme.palette.text.disabled, 0.6),
                                ':hover': {
                                    backgroundColor: '#E8EEF6',
                                },
                                '.MuiButton-startIcon': {
                                    marginLeft: 0,
                                },
                            })}
                        >
                            {t('Remove')}
                        </Button>
                    </Box>
                ) : null}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                    }}
                >
                    <CatalogProductTable>
                        <CatalogProductTableHead
                            leading={
                                canManage ? (
                                    <Checkbox
                                        disableRipple
                                        sx={{ padding: 0 }}
                                        checked={allVisibleSelected}
                                        indeterminate={
                                            someVisibleSelected &&
                                            !allVisibleSelected
                                        }
                                        onChange={onToggleAll}
                                    />
                                ) : undefined
                            }
                            quantityColumnLabel={t('customLists.defaultQty')}
                            showTrailing={canManage}
                        />
                        <TableBody>
                            {items.map((listItem) => {
                                const catalogItem =
                                    getCustomListCatalogItem(listItem);

                                return (
                                    <CatalogProductTableRow
                                        key={listItem.id}
                                        dataTestClass="custom-list-item-row"
                                        onClick={
                                            catalogItem
                                                ? () =>
                                                      onExpandItem(catalogItem)
                                                : undefined
                                        }
                                        product={{
                                            title: catalogItem?.title,
                                            thumbnailUrl:
                                                catalogItem?.thumbnailUrl,
                                            imageUrl: catalogItem?.imageUrl,
                                            categoryId: catalogItem?.categoryId,
                                            uom: catalogItem?.uom,
                                            proposals: catalogItem?.proposals,
                                        }}
                                        leading={
                                            canManage ? (
                                                <Checkbox
                                                    disableRipple
                                                    sx={{ padding: 0 }}
                                                    checked={selectedIds.includes(
                                                        listItem.id,
                                                    )}
                                                    onChange={() =>
                                                        onToggleRow(listItem.id)
                                                    }
                                                />
                                            ) : undefined
                                        }
                                        quantityCell={
                                            <CustomListItemQuantityCell
                                                listId={listId}
                                                itemId={listItem.id}
                                                value={
                                                    listItem.targetQuantity || 1
                                                }
                                                disabled={!canManage}
                                            />
                                        }
                                        trailing={
                                            canManage ? (
                                                <IconButton
                                                    data-testclass="custom-list-row-remove"
                                                    aria-label={t('Remove')}
                                                    size="small"
                                                    onClick={(e) =>
                                                        onOpenRemoveMenu(e, [
                                                            listItem.id,
                                                        ])
                                                    }
                                                    sx={{
                                                        padding: '4px',
                                                        color: 'rgba(0, 0, 0, 0.54)',
                                                        '&:hover': {
                                                            bgcolor:
                                                                'transparent',
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                        },
                                                    }}
                                                >
                                                    <DeleteOutline
                                                        sx={{
                                                            width: '24px',
                                                            height: '24px',
                                                        }}
                                                    />
                                                </IconButton>
                                            ) : undefined
                                        }
                                    />
                                );
                            })}
                        </TableBody>
                    </CatalogProductTable>
                </Box>
            </Box>
        );
    },
);

CustomListDetailItemsTable.displayName = 'CustomListDetailItemsTable';
